import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"

export type StandardErrorType = {
  created_at: Date
  traceback_uuid: string
  errors: {
    code: string
    message: string
    detail: string
  }[]
}

type ErrorHandler = ((e: AxiosError<StandardErrorType>) => void) | undefined

export const defaultErrorHandler = (e: AxiosError<StandardErrorType>): void => {
  // show more specific message in the case of network errors
  if (e.code === "ERR_NETWORK") {
    console.error("Network Error")
  }

  // Ignore error in the case of errors with status codes 401, 403 or 404
  if (e.status === 401 || e.status === 403 || e.status === 404) {
    return
  }

  const errors = e.response?.data?.errors

  // Check if e.errors is an array of objects with code, message, and detail
  const isValidErrorArray =
    Array.isArray(errors) &&
    errors.length > 0 &&
    typeof errors[0] === "object" &&
    "code" in errors[0] &&
    "message" in errors[0] &&
    "detail" in errors[0]

  if (isValidErrorArray) {
    const tracebackID = e.response?.data?.traceback_uuid

    errors.forEach((e) => {
      return NotificationUtils.toast(e.detail, {
        severity: "error",
        action: tracebackID
          ? {
              title: "Copy ID",
              onClick: () => {
                navigator.clipboard.writeText(tracebackID)
                NotificationUtils.toast("Error traceback ID copied to clipboard", {
                  severity: "success",
                })
              },
            }
          : undefined,
      })
    })
  } else {
    // Fallback for other error formats
    NotificationUtils.toast("Something went wrong", {
      severity: "error",
    })
  }
}

export const withErrorHandler = (localOnError?: ErrorHandler): ErrorHandler => {
  return (e: AxiosError<StandardErrorType>) => {
    defaultErrorHandler(e)
    localOnError?.(e)
  }
}
